import { useEffect, useRef } from "react";
import moment from "moment";
import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiList,
  MiLoader,
  MiMenu,
  MiStackedTypography,
} from "@miplatform/components";
import { PAGINATION, TYPOGRAPHY_VARIANTS } from "@miplatform/constants";
import { useBoolean, useDebounce } from "@miplatform/hooks";
import {
  ButtonVariants,
  IAttemptListItemResponseForWorkOrder,
  LoaderType,
} from "@miplatform/types";
import { getTimeDifferenceDurationText } from "@miplatform/utils";
import {
  mdiCalendarStart,
  mdiFormatListBulleted,
  mdiInformationSlabCircleOutline,
} from "@mdi/js";
import { Box, Divider, Tooltip, Typography, useTheme } from "@mui/material";
import { MiWorkOrderTooltip } from "../../../components/MiWorkOrderCard/MiWorkOrderTooltip";
import { useCalendarContext } from "../../../providers";
import { OrderCardOrderTypeField } from "../../Orders/OrdersList/OrderCardOrderTypeField/OrderCardOrderTypeField";
import { WorkOrderForceReleaseModal } from "./WorkOrderForceReleaseModal";
import { useAutoHoldListViewModel } from "./useAutoHoldListViewModel";

const PLACEHOLDER_TEXT = "--";

const HOLD_REASON_MAP = {
  AUTO_HOLD_DUE_TO_ORDER_HOLD: "Hold due to order hold",
};

export const AutoHoldList = () => {
  const didMountRef = useRef(false);
  const { calendarConfig, search, setSelectedAttempt, getCalendarCounts } =
    useCalendarContext();
  const {
    isLoading,
    refresh,
    attempts,
    getAttempts,
    setAttempts,
    setRefresh,
    releaseWorkOrder,
  } = useAutoHoldListViewModel({
    calendarId: calendarConfig?.id as string,
    workOrderTemplates: calendarConfig?.workTemplateBases || [],
    filter: calendarConfig?.filter,
    search,
    onRefresh: getCalendarCounts,
  });
  const debouncedSearch = useDebounce(search);

  const { classes, theme } = useStyles();

  useEffect(() => {
    if (didMountRef.current) {
      setRefresh((prev) => prev + 1);
    } else {
      didMountRef.current = true;
    }
  }, [debouncedSearch]);

  const sections = [
    {
      fieldsetStyle: {
        alignItems: "center",
      },
      fieldset: [
        {
          field: "priority",
          renderCell: ({
            assignment,
          }: IAttemptListItemResponseForWorkOrder) => {
            const {
              holdReason,
              holdStartDate,
              abbreviation,
              workOrderTemplateColor,
              workOrderTemplateName,
            } = assignment?.workOrder || {};
            const chipLabel =
              abbreviation || workOrderTemplateName?.slice(0, 3) || "";

            return (
              <Box className={classes.items}>
                <MiStackedTypography
                  title="Hold days"
                  description={getTimeDifferenceDurationText({
                    start: moment(holdStartDate),
                    end: moment.now(),
                  })}
                  descriptionProps={{
                    width: 130,
                    fontWeight: theme.fontWeight.bold,
                  }}
                  style={{ flex: 1 }}
                />
                <MiStackedTypography
                  title="Hold Reason"
                  description={
                    HOLD_REASON_MAP[
                      holdReason as keyof typeof HOLD_REASON_MAP
                    ] ||
                    holdReason ||
                    PLACEHOLDER_TEXT
                  }
                  descriptionProps={{
                    width: 260,
                    fontWeight: theme.fontWeight.bold,
                  }}
                  style={{ flex: 1 }}
                />
                <Box flex={1}>
                  <OrderCardOrderTypeField
                    title="Work Order"
                    orderTemplateAbbreviation={chipLabel}
                    orderTemplateColor={workOrderTemplateColor as string}
                    orderTemplateName={workOrderTemplateName as string}
                    orderTemplateShortName={chipLabel}
                  />
                </Box>
              </Box>
            );
          },
        },
      ],
    },
  ];

  return (
    <Box className={classes.root}>
      <MiLoader isLoading={isLoading} type={LoaderType.Linear} />
      <MiList
        refresh={refresh}
        viewType="card"
        loading={isLoading}
        paginationMode={PAGINATION.SERVER}
        dataProvider={getAttempts}
        setData={setAttempts}
        cardProps={{
          containerStyles: { backgroundColor: theme.palette.grey.light },
          sections,
          renderTitle: (attempt: IAttemptListItemResponseForWorkOrder) => (
            <Box className={classes.titleSection}>
              <Box className={classes.titleChildSection}>
                <Typography
                  color="main.primary"
                  fontWeight={600}
                  fontSize={14}
                  noWrap
                >
                  {attempt.home?.address?.addressLine1 || PLACEHOLDER_TEXT}
                </Typography>
                <Typography
                  variant={TYPOGRAPHY_VARIANTS.BODY2}
                  color="textSecondary"
                  noWrap
                >
                  {attempt.home?.builderName}
                </Typography>
                <Tooltip
                  data-testid="order-info-tooltip"
                  title={<MiWorkOrderTooltip attempt={attempt} />}
                  slotProps={{
                    popper: {
                      placement: "right",
                      modifiers: [
                        {
                          name: "offset",
                          options: { offset: [0, -18] },
                        },
                      ],
                      sx: { p: 0, m: 0 },
                    },
                    tooltip: { sx: { p: 0, m: 0 } },
                  }}
                >
                  <Box ml={-1}>
                    <MiButton
                      size="small"
                      className={classes.infoButton}
                      variant={ButtonVariants.Contained}
                      startIcon={{
                        path: mdiInformationSlabCircleOutline,
                        iconSize: 0.5,
                      }}
                    />
                  </Box>
                </Tooltip>
              </Box>
              <Box className={classes.titleChildSection}>
                <Typography
                  variant={TYPOGRAPHY_VARIANTS.BODY2}
                  color="textSecondary"
                  noWrap
                >
                  {attempt.home?.address?.city || PLACEHOLDER_TEXT},{" "}
                  {attempt.home?.address?.regionCode || PLACEHOLDER_TEXT}
                </Typography>
                <Divider orientation="vertical" flexItem />
                <Typography
                  variant={TYPOGRAPHY_VARIANTS.BODY2}
                  color="textSecondary"
                  noWrap
                >
                  {attempt.home?.communityName || "(No Community Info)"}
                </Typography>
              </Box>
            </Box>
          ),
          renderItemActions: (
            attempt: IAttemptListItemResponseForWorkOrder
          ) => (
            <AutoHoldItemActions
              attempt={attempt}
              onDetailsClick={setSelectedAttempt}
              onRelease={releaseWorkOrder}
            />
          ),
        }}
        data={attempts}
      />
    </Box>
  );
};

const AutoHoldItemActions = ({
  attempt,
  onDetailsClick,
  onRelease,
}: {
  attempt: IAttemptListItemResponseForWorkOrder;
  onDetailsClick: (attempt: IAttemptListItemResponseForWorkOrder) => void;
  onRelease: (
    attempt: IAttemptListItemResponseForWorkOrder,
    reason: string
  ) => void;
}) => {
  const theme = useTheme();
  const [isReleaseModalOpen, handleReleaseModalOpen, handleReleaseModalClose] =
    useBoolean(false);

  const handleRelease = (reason: string) => {
    handleReleaseModalClose();
    onRelease(attempt, reason);
  };

  return (
    <>
      <MiMenu
        data={[
          {
            label: "Details",
            icon: mdiFormatListBulleted,
            color: theme.palette.grey.main,
            action: () => onDetailsClick(attempt),
          },
          {
            label: "Force Release",
            icon: mdiCalendarStart,
            color: theme.palette.green.main,
            action: handleReleaseModalOpen,
          },
        ]}
      />
      <WorkOrderForceReleaseModal
        isOpen={isReleaseModalOpen}
        onClose={handleReleaseModalClose}
        onRelease={handleRelease}
      />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    height: "100%",
    overflowY: "auto",
  },
  items: {
    display: "flex",
    gap: theme.spacing(4),
  },
  titleSection: {
    display: "flex",
    flexDirection: "column",
    maxWidth: 250,
  },
  titleChildSection: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  fields: { display: "flex", flexDirection: "column", gap: theme.spacing(2) },
  infoButton: {
    backgroundColor: theme.palette.blue.dark,
    padding: theme.spacing(0.3, 1.5),
    marginLeft: theme.spacing(1),
  },
}));
